import { useCallback } from 'react'

import SubscriptionEvents from 'Pods/Shared/Constants/subscriptionEvents'
import useTracker from 'Pods/Shared/Hooks/useTracker'

const PAGE_TYPE = 'subscription_settings'

const useSubscriptionSettingsTracking = (tier = null) => {
  const { trackEvent } = useTracker()

  const trackSectionView = useCallback(() => {
    trackEvent(SubscriptionEvents.SETTINGS_VIEWED, { page_type: PAGE_TYPE, subscription_tier: tier })
  }, [trackEvent, tier])

  const trackCancelClick = useCallback(() => {
    trackEvent(SubscriptionEvents.CANCEL_CLICK, { page_type: PAGE_TYPE, subscription_tier: tier })
  }, [trackEvent, tier])

  const trackCancellationConfirmed = useCallback((reason, subscriptionId, customReason) => {
    const payload = {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      reason,
      subscription_id: subscriptionId,
    }

    if (customReason) {
      payload.custom_reason = customReason
    }

    trackEvent(SubscriptionEvents.CANCELLATION_CONFIRMED, payload)
  }, [trackEvent, tier])

  const trackRenewClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.RENEW_CLICK, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackRenewSuccess = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.RENEW_SUCCESS, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackRenewFailure = useCallback((subscriptionId, errorMessage) => {
    trackEvent(SubscriptionEvents.RENEW_FAILURE, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
      error_message: errorMessage,
    })
  }, [trackEvent, tier])

  const trackReactivateClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.REACTIVATE_CLICK, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackReactivateSuccess = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.REACTIVATE_SUCCESS, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackReactivateFailure = useCallback((subscriptionId, errorMessage) => {
    trackEvent(SubscriptionEvents.REACTIVATE_FAILURE, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
      error_message: errorMessage,
    })
  }, [trackEvent, tier])

  const trackPauseOfferViewed = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_OFFER_VIEWED, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackPauseClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_CLICK, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackPauseScheduled = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_SCHEDULED, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackPauseFailure = useCallback((subscriptionId, errorCode) => {
    trackEvent(SubscriptionEvents.PAUSE_FAILURE, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
      error_message: errorCode,
    })
  }, [trackEvent, tier])

  const trackPauseResumeClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_RESUME_CLICK, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackPauseResumeSuccess = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_RESUME_SUCCESS, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackPauseResumeFailure = useCallback((subscriptionId, errorCode) => {
    trackEvent(SubscriptionEvents.PAUSE_RESUME_FAILURE, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
      error_message: errorCode,
    })
  }, [trackEvent, tier])

  const trackRetryClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.RETRY_PAYMENT_CLICK, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
    })
  }, [trackEvent, tier])

  const trackPaymentHistoryViewed = useCallback(() => {
    trackEvent(SubscriptionEvents.PAYMENT_HISTORY_VIEWED, { page_type: PAGE_TYPE, subscription_tier: tier })
  }, [trackEvent, tier])

  const trackInvoiceDownloadClick = useCallback((subscriptionId, paymentId) => {
    trackEvent(SubscriptionEvents.INVOICE_DOWNLOAD_CLICK, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
      payment_id: paymentId,
    })
  }, [trackEvent, tier])

  const trackInvoiceDownloadFailed = useCallback((subscriptionId, paymentId, errorMessage) => {
    trackEvent(SubscriptionEvents.INVOICE_DOWNLOAD_FAILED, {
      page_type: PAGE_TYPE,
      subscription_tier: tier,
      subscription_id: subscriptionId,
      payment_id: paymentId,
      error_message: errorMessage,
    })
  }, [trackEvent, tier])

  return {
    trackSectionView,
    trackCancelClick,
    trackCancellationConfirmed,
    trackRenewClick,
    trackRenewSuccess,
    trackRenewFailure,
    trackReactivateClick,
    trackReactivateSuccess,
    trackReactivateFailure,
    trackRetryClick,
    trackPauseOfferViewed,
    trackPauseClick,
    trackPauseScheduled,
    trackPauseFailure,
    trackPauseResumeClick,
    trackPauseResumeSuccess,
    trackPauseResumeFailure,
    trackPaymentHistoryViewed,
    trackInvoiceDownloadClick,
    trackInvoiceDownloadFailed,
  }
}

export default useSubscriptionSettingsTracking
